import {Chart} from './chart';
import {Point} from '../../types/primitives';
import {Helpers} from '../common/helpers';
import {Constants as constants} from '../config/constants';
import {Selection as D3Selection} from 'd3';

export class PathRenderer {
    private center = null as unknown as Point;
    private arcs = {
        small: null as unknown as { start: Point, path: string, end: Point },
        middle: null as unknown as { start: Point, path: string, end: Point },
        large: null as unknown as { start: Point, path: string, end: Point }
    };
    private upperLimitArcs = {
        small: null as unknown as { start: Point, path: string, end: Point },
        middle: null as unknown as { start: Point, path: string, end: Point },
        large: null as unknown as { start: Point, path: string, end: Point }
    };

    private mainArc = null as unknown as D3Selection<any, any, any, any>;
    private upperArc = null as unknown as D3Selection<any, any, any, any>;

    constructor(private chart: Chart) {
        this.calculateCenter();
        this.calculateArcs();
        this.calculateUpperArcs();
    }

    public render() {
        this.drawArcs();
    }

    private calculateCenter() {
        this.center = Helpers.clientToSvgPoint(
            this.chart.node.svg.node(),
            this.chart.node.chart.node(),
            {
                x: window.innerWidth / 2,
                y: window.innerHeight / 2
            }
        );
    }

    private calculateArcs() {
        const startPoint = {
            x: this.center.x,
            y: this.center.y
        };

        this.arcs.middle = Helpers.describeArc(
            startPoint,
            constants.arcs.middle.r,
            constants.arcs.small.angle + constants.arcs.large.angle,
            360
        );
        this.arcs.large = Helpers.describeArc(
            startPoint,
            constants.arcs.large.r,
            0,
            constants.arcs.large.angle
        );
        this.arcs.small = Helpers.describeArc(
            startPoint,
            constants.arcs.small.r,
            constants.arcs.large.angle,
            constants.arcs.large.angle + constants.arcs.small.angle
        );
    }

    private calculateUpperArcs() {
        const startPoint = {
            x: this.center.x,
            y: this.center.y
        };

        this.upperLimitArcs.middle = Helpers.describeArc(
            startPoint,
            constants.arcs.middle.r + constants.arcDiffStep.r,
            constants.arcs.small.angle + constants.arcs.large.angle - Helpers.calculateArcAngle(constants.arcs.middle.r + constants.arcDiffStep.r, constants.arcDiffStep.r),
            360 - Helpers.calculateArcAngle(constants.arcs.middle.r + constants.arcDiffStep.r, constants.arcDiffStep.r)
        );
        this.upperLimitArcs.large = Helpers.describeArc(
            startPoint,
            constants.arcs.large.r + constants.arcDiffStep.r,
            360 - Helpers.calculateArcAngle(constants.arcs.large.r + constants.arcDiffStep.r, constants.arcDiffStep.r),
            constants.arcs.large.angle + Helpers.calculateArcAngle(constants.arcs.large.r + constants.arcDiffStep.r, constants.arcDiffStep.r)
        );
        this.upperLimitArcs.small = Helpers.describeArc(
            startPoint,
            constants.arcs.small.r + constants.arcDiffStep.r,
            constants.arcs.large.angle + Helpers.calculateArcAngle(constants.arcs.small.r + constants.arcDiffStep.r, constants.arcDiffStep.r),
            constants.arcs.large.angle + constants.arcs.small.angle - Helpers.calculateArcAngle(constants.arcs.small.r + constants.arcDiffStep.r, constants.arcDiffStep.r)
        );
    }

    private drawArcs() {
        const mainPath = [
            'M', this.arcs.middle.path,
            'M', `${this.arcs.middle.start.x} ${this.arcs.middle.start.y}`,
            'L', `${this.arcs.large.end.x} ${this.arcs.large.end.y}`,
            'M', this.arcs.large.path,
            'M', `${this.arcs.large.start.x} ${this.arcs.large.start.y}`,
            'L', `${this.arcs.small.end.x} ${this.arcs.small.end.y}`,
            'M', this.arcs.small.path,
            'M', `${this.arcs.small.start.x} ${this.arcs.small.start.y}`,
            'L', `${this.arcs.middle.end.x} ${this.arcs.middle.end.y}`
        ].join(' ');
        const upperPath = [
            'M', this.upperLimitArcs.middle.path,
            'M', `${this.upperLimitArcs.middle.start.x} ${this.upperLimitArcs.middle.start.y}`,
            'L', `${this.upperLimitArcs.large.end.x} ${this.upperLimitArcs.large.end.y}`,
            'M', this.upperLimitArcs.large.path,
            'M', `${this.upperLimitArcs.large.start.x} ${this.upperLimitArcs.large.start.y}`,
            'L', `${this.upperLimitArcs.small.end.x} ${this.upperLimitArcs.small.end.y}`,
            'M', this.upperLimitArcs.small.path,
            'M', `${this.upperLimitArcs.small.start.x} ${this.upperLimitArcs.small.start.y}`,
            'L', `${this.upperLimitArcs.middle.end.x} ${this.upperLimitArcs.middle.end.y}`
        ].join(' ');

        this.mainArc = this.chart.node.chart
            .append('path')
            .attr('d', mainPath)
            .attr('class', 'mainArc');
        this.upperArc = this.chart.node.chart
            .append('path')
            .attr('d', upperPath)
            .attr('class', 'upperArc');
    }

    public get data() {
        return {
            center: this.center,
            arc: this.arcs,
            upperArc: this.upperArc
        };
    }
}
